import { InfoPage } from '@/components/info-page.js';
import { getCoachSessionPayment, getUser } from '@/lib/api-calls.js';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { InfoCard } from '@/components/info-card.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { airColumnHelper } from '@/lib/tables.js';
import {
  CoachPaymentSessionDetails,
  coachPaymentSessionDetailsSchema,
  formatCoachSessionRemarkV2Label,
  PayCycleDTO,
  VideoConferenceTypeLabels,
} from '@airreading/common/data';
import { EntityTable } from '@/components/table/entity-table.js';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { formatInstant, formatPlainDate, FrequentTimeZones } from '@airreading/common';
import { formatCurrency } from '@/lib/common-utils.js';
import { SessionCompletionStatusCell } from '@/routes/_layout/sessions/-components/session-completion-status-cell.js';
import { SessionFeedbackStatus } from '@/components/session/session-feedback-status.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { Badge } from '@/components/ui/badge.js';
import { TimeZoneTruncated } from '@/components/time-zone-truncated.js';

export const Route = createFileRoute('/_layout/teachers/$teacherId/payments/teaching/$payCycleId/')({
  component: TeachSessionPaymentsDetails,
  validateSearch: paginationRequestSchema,
});

const columnHelper = airColumnHelper<CoachPaymentSessionDetails>(coachPaymentSessionDetailsSchema.keyof());

const coachPaymentReportTableColumns = [
  columnHelper.accessor((row) => row, {
    id: 'classTime',
    header: () => (
      <span className="flex items-centerwhitespace-nowrap">
        Time (<TimeZoneTruncated timeZone={FrequentTimeZones.UsCentral} />)
      </span>
    ),

    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const startDateTime = formatInstant(session.startInstant, FrequentTimeZones.UsCentral);
      const endDateTime = formatInstant(session.endInstant, FrequentTimeZones.UsCentral);

      const [month, day] = startDateTime.formattedDate.split('/');

      return `${month}/${day}, ${startDateTime.formattedTime} - ${endDateTime.formattedTime}`;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'duration',
    header: 'Session duration',

    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const duration = session.endInstant.since(session.startInstant);

      const minutes = duration.total({ unit: 'minute' });

      return `${minutes} mins (${VideoConferenceTypeLabels[session.videoConferenceType]})`;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'meetingStatus',
    header: 'Meeting status',
    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const { sessionCompletionStatus, coachSessionRemarksV2 } = session;

      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={session}
        />
      );
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'feedback',
    header: 'Feedback',
    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const { hoursCanceledBeforeStart, sessionFeedbacks, lesson } = session;

      return (
        <SessionFeedbackStatus
          feedbacks={sessionFeedbacks || []}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
        />
      );
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'pay',
    header: 'Pay',

    cell: ({ getValue }) => {
      const session = getValue();

      return formatCurrency((session?.baseAmount ?? 0) / 100);
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'incentive',
    header: 'Incentive',

    cell: ({ getValue }) => {
      const session = getValue();

      return formatCurrency((session?.incentivesAmount ?? 0) / 100);
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'total',
    header: 'Total',

    cell: ({ getValue }) => {
      const session = getValue();

      const total = session?.baseAmount + session?.incentivesAmount;

      return formatCurrency((total ?? 0) / 100);
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'remarks',
    header: 'Session remark',

    cell: ({ getValue }) => {
      const session = getValue();

      if (!session) {
        return '-';
      }

      const { coachSessionRemarksV2 } = session;

      return (
        <div className="flex flex-col gap-1 items-center">
          {coachSessionRemarksV2?.map((tag) => (
            <Badge key={tag} variant="outline" className="text-center">
              {formatCoachSessionRemarkV2Label(tag, session)}
            </Badge>
          ))}
        </div>
      );
    },
  }),
];

function TeachSessionPaymentsDetails() {
  const { teacherId, payCycleId } = Route.useParams();

  const { data: user } = useQuery({
    queryKey: ['user', teacherId],
    queryFn: () => getUser(teacherId),
  });

  const { pageIndex, pageSize, onPaginationChange } = usePaginatedFilters(Route.id, ['pageIndex', 'pageSize']);

  const { data, error, isPending, refetch } = useQuery(
    getCoachSessionPayment({
      coachId: teacherId,
      filters: {
        pageIndex,
        pageSize,
        filters: {
          payCycleId,
        },
      },
    }),
  );

  useErrorToast(error, 'Error loading payments. Please try again');

  const table = useReactTable({
    data: data?.payments || [],
    columns: coachPaymentReportTableColumns,
    getRowId: (row) => row.id,
    rowCount: data?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    },
  });

  return (
    <InfoPage
      headerText={user?.fullNameOrEmail || 'Payments'}
      data-testid="payments-teaching-page"
      breadcrumbs={{
        linkItems: [
          {
            label: 'Teachers',
            link: {
              to: '/teachers',
            },
          },
          {
            label: 'Teacher details',
            link: {
              to: '/teachers/$teacherId/payments',
            },
          },
        ],
        currentPage: 'Payments',
      }}
    >
      <InfoCard headerText={'Teaching'} data-testid="payments-teaching-summary">
        <InfoKeyValue
          label={'Gross / Net Earnings'}
          value={formatCurrency((data?.totalAmountInCents ?? 0) / 100)}
          data-testid="payments-teaching-summary-gross-net-earnings"
        />
        <InfoKeyValue
          label={'Period'}
          value={`
            ${formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).startPlainDate)} - ${formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).endPlainDate)}
            `}
          data-testid="payments-teaching-summary-period"
        />
        <InfoKeyValue
          label={'Pay Initiation Date'}
          value={formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).paymentPlainDate)}
          data-testid="payments-teaching-summary-pay-initiation-date"
        />
        <InfoKeyValue
          label={'Delivery (1-3 business days)'}
          value={data?.depositPostedDate}
          data-testid="payments-teaching-summary-delivery"
        />
      </InfoCard>
      <EntityTable
        table={table}
        showPagination
        data-testid="payments-teaching-table"
        rowTestId={(_, rowIndex) => `payments-teaching-table-row-${rowIndex}`}
        cellTestId={(_, columnId, rowIndex) => `payments-teaching-table-cell-${columnId}-${rowIndex}`}
        endMessageTestId="payments-teaching-table-end-message"
        isLoading={isPending}
        hasError={!!error}
        emptyState="No payments found"
        errorState={<TableErrorState refetchAction={refetch} />}
      />
    </InfoPage>
  );
}
